import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Subheading from '../font/Subheading';
import Modal from '../overlay/Modal';
import { useToast } from '../toast/ToastProvider';
import { JOB_DONE, ShopifyJob, ShopifyJobsResponse } from '../../models/shopify';
import { deleteShopifyJob, getShopifyJobs, markShopifyJobDone } from '../../requests/shopify.request';

const formatDate = (value?: string | null) => value ? new Date(value).toLocaleString() : '—';

const errorMessage = (error: unknown, fallback: string) => {
  const message = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof message === 'string' ? message : fallback;
};

const describe = (job: ShopifyJob) => `job ${job.job_id ?? job.id}`;

type Props = { shopId: string };

// Rows of product_shopify.job_history for the shop. CS can mark a job's status DONE or delete it.
const JobHistory = ({ shopId }: Props) => {
  const toast = useToast();
  const [result, setResult] = useState<ShopifyJobsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const [deleteTarget, setDeleteTarget] = useState<ShopifyJob | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const current = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const response = await getShopifyJobs(shopId);
      if (current === requestId.current) setResult(response);
    } catch (loadError) {
      if (current === requestId.current) {
        setResult(null);
        setError(errorMessage(loadError, 'Could not load the job history.'));
      }
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  }, [shopId]);

  useEffect(() => {
    setResult(null);
    load();
  }, [load]);

  const markDone = async (job: ShopifyJob) => {
    setSaving(true);
    try {
      await markShopifyJobDone(shopId, job.id);
      toast.show(`Marked ${describe(job)} as ${JOB_DONE}`);
    } catch (mutationError) {
      toast.show(errorMessage(mutationError, 'Could not update the job.'), { isError: true });
    } finally {
      setSaving(false);
      load();
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteShopifyJob(shopId, deleteTarget.id);
      toast.show(`Deleted ${describe(deleteTarget)}`);
    } catch (mutationError) {
      toast.show(errorMessage(mutationError, 'Could not delete the job.'), { isError: true });
    } finally {
      setSaving(false);
      setDeleteTarget(null);
      load();
    }
  };

  const jobs = result?.jobs;

  return (
    <s-section>
      <SectionBody>
        <Header>
          <Subheading>{result ? `Job history (${result.count})` : 'Job history'}</Subheading>
          <s-button icon='refresh' onClick={load} disabled={loading} accessibilityLabel='Reload job history'>Reload</s-button>
        </Header>
        {error && <s-banner tone='critical'><p>{error}</p></s-banner>}
        {loading && !result && <Loading><s-spinner accessibilityLabel='Loading job history' /></Loading>}
        {jobs && !jobs.length && <Muted>No jobs recorded for this shop.</Muted>}
        {jobs && result.count > jobs.length && <Muted>Showing the newest {jobs.length} of {result.count} jobs.</Muted>}
        {jobs && !!jobs.length && (
          <s-table>
            <s-table-header-row>
              <s-table-header listSlot='primary'>Job ID</s-table-header>
              <s-table-header listSlot='secondary'>Type</s-table-header>
              <s-table-header listSlot='labeled'>Status</s-table-header>
              <s-table-header listSlot='labeled' format='numeric'>Total</s-table-header>
              <s-table-header listSlot='labeled'>Created</s-table-header>
              <s-table-header listSlot='labeled'>Updated</s-table-header>
              <s-table-header listSlot='inline'><VisuallyHidden>Actions</VisuallyHidden></s-table-header>
            </s-table-header-row>
            <s-table-body>
              {jobs.map(job => (
                <s-table-row key={job.id}>
                  <s-table-cell><Code>{job.job_id ?? '—'}</Code></s-table-cell>
                  <s-table-cell>
                    <Code>{job.type ?? '—'}</Code>
                  </s-table-cell>
                  <s-table-cell>
                    {job.status === JOB_DONE ? <s-badge tone='success'>{JOB_DONE}</s-badge> : <Wrap>{job.status ?? '—'}</Wrap>}
                  </s-table-cell>
                  <s-table-cell>{job.total ?? '—'}</s-table-cell>
                  <s-table-cell>{formatDate(job.created_at)}</s-table-cell>
                  <s-table-cell>{formatDate(job.updated_at)}</s-table-cell>
                  <s-table-cell>
                    <RowActions>
                      <s-button variant='tertiary' onClick={() => markDone(job)}
                        disabled={saving || job.status === JOB_DONE}
                        accessibilityLabel={`Mark ${describe(job)} as ${JOB_DONE}`}>Mark {JOB_DONE}</s-button>
                      <s-button variant='tertiary' tone='critical' icon='delete' onClick={() => setDeleteTarget(job)}
                        accessibilityLabel={`Delete ${describe(job)}`} />
                    </RowActions>
                  </s-table-cell>
                </s-table-row>
              ))}
            </s-table-body>
          </s-table>
        )}
      </SectionBody>

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        heading='Delete job?'
        primaryAction={{ content: 'Delete', destructive: true, loading: saving, onAction: remove }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setDeleteTarget(null) }]}
      >
        <p>
          {deleteTarget && `This removes ${describe(deleteTarget)} (${deleteTarget.type ?? 'no type'}) from the job history.`}
          {' '}It can be restored from the History page.
        </p>
      </Modal>
    </s-section>
  );
};

export default JobHistory;

const SectionBody = styled.div`
  display: grid;
  gap: 12px;
  min-width: 0;
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Loading = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
`;

const Muted = styled.p`
  margin: 0;
  color: #6d7175;
`;

const Code = styled.code`
  font-size: 12px;
  overflow-wrap: anywhere;
`;

const Wrap = styled.span`
  overflow-wrap: anywhere;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;
